import React, { useState } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import { Box, Button, MenuItem, Stack, Typography } from '@mui/material';
import ImageOutlined from '@mui/icons-material/ImageOutlined';
import { useTranslate } from 'react-admin';
import { AttrTextField, stopEditorEvents } from './AttrFields';
import { MediaPickerDialog, type PickedMedia } from '../../../../common/MediaPickerDialog';
import { useMediaObjectUrl } from '../../../../../hooks/useMediaObjectUrl';

/**
 * Editor node view of the image block: picture from the media library plus
 * alt text, caption and alignment.
 */
export const ImageBlockView: React.FC<NodeViewProps> = ({ node, selected, updateAttributes }) => {
    const translate = useTranslate();
    const { src, alt, caption, alignment, mediaFileUuid } = node.attrs as {
        src: string;
        alt: string;
        caption: string;
        alignment: string;
        mediaFileUuid: string;
    };
    const [pickerOpen, setPickerOpen] = useState(false);
    const objectUrl = useMediaObjectUrl(src);

    const onPick = (media: PickedMedia) => {
        updateAttributes({ src: `/api/media/${media.uuid}`, mediaFileUuid: media.uuid, alt: alt || media.originalFilename });
    };

    const justify = alignment === 'left' ? 'flex-start' : alignment === 'right' ? 'flex-end' : 'center';

    return (
        <NodeViewWrapper className={`oa-block oa-block-image${selected ? ' is-selected' : ''}`} data-type="image-block">
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: justify, gap: 1 }}>
                {src ? (
                    <img src={objectUrl ?? src} alt={alt} className="oa-block-image-img" loading="lazy" />
                ) : (
                    <Box className="oa-block-dropzone" onClick={() => setPickerOpen(true)}>
                        <ImageOutlined sx={{ fontSize: 32, color: 'var(--oa-text-2)' }} />
                        <Typography variant="body2" color="text.secondary">{translate('blockEditor.image.empty')}</Typography>
                        <Button size="small" variant="outlined">{translate('blockEditor.image.fromLibrary')}</Button>
                    </Box>
                )}
                {caption && (
                    <Typography variant="caption" color="text.secondary" component="figcaption">{caption}</Typography>
                )}
            </Box>

            {selected && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }} {...stopEditorEvents}>
                    <AttrTextField size="small" label={translate('blockEditor.image.alt')} value={alt} onValueChange={(v) => updateAttributes({ alt: v })} fullWidth />
                    <AttrTextField size="small" label={translate('blockEditor.image.caption')} value={caption} onValueChange={(v) => updateAttributes({ caption: v })} fullWidth />
                    <AttrTextField
                        size="small"
                        select
                        label={translate('blockEditor.image.alignment.label')}
                        value={alignment || 'center'}
                        onValueChange={(v) => updateAttributes({ alignment: v })}
                        sx={{ minWidth: 140 }}
                    >
                        <MenuItem value="left">{translate('blockEditor.image.alignment.left')}</MenuItem>
                        <MenuItem value="center">{translate('blockEditor.image.alignment.center')}</MenuItem>
                        <MenuItem value="right">{translate('blockEditor.image.alignment.right')}</MenuItem>
                    </AttrTextField>
                    <Button size="small" onClick={() => setPickerOpen(true)} sx={{ whiteSpace: 'nowrap' }}>
                        {src ? translate('blockEditor.image.replace') : translate('blockEditor.image.fromLibrary')}
                    </Button>
                </Stack>
            )}

            <MediaPickerDialog open={pickerOpen} onClose={() => setPickerOpen(false)} accept="image" onSelect={onPick} selectedUuid={mediaFileUuid} />
        </NodeViewWrapper>
    );
};
